import { s3, BUCKET_NAME } from "../config/minio";

// Get bucket URL from environment or use default
const BUCKET_URL =
  process.env.REACT_APP_MINIO_BUCKET_URL || "https://s3.blocsys.com/vestn";

export interface UploadResult {
  path: string;
  url: string;
  key: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

/**
 * Upload a file to MinIO (S3-compatible storage)
 * @param file - File object or Buffer to upload
 * @param key - Unique key/path for the file in the bucket
 * @param onProgress - Optional progress callback
 * @returns Promise with upload result containing path, url, and key
 */
export const uploadToMinIO = async (
  file: File | Buffer,
  key: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> => {
  try {
    // Ensure bucket exists
    await ensureBucketExists();

    // Prepare upload parameters
    const uploadParams: AWS.S3.PutObjectRequest = {
      Bucket: BUCKET_NAME,
      Key: key,
      Body: file,
      ContentType: getContentType(file),
      ACL: "public-read", // Make file publicly accessible
    };

    // Upload with progress tracking
    const upload = s3.upload(uploadParams);

    if (onProgress) {
      upload.on("httpUploadProgress", (progress: any) => {
        const percentage = Math.round((progress.loaded / progress.total) * 100);
        onProgress({
          loaded: progress.loaded,
          total: progress.total,
          percentage,
        });
      });
    }

    const result = await upload.promise();

    return {
      path: result.Key,
      url: `${BUCKET_URL}/${result.Key}`,
      key: result.Key,
    };
  } catch (error) {
    console.error("Error uploading to MinIO:", error);
    throw new Error(`Failed to upload file: ${error}`);
  }
};

/**
 * Delete a file from MinIO
 * @param key - The key/path of the file to delete
 */
export const deleteFromMinIO = async (key: string): Promise<void> => {
  try {
    const deleteParams: AWS.S3.DeleteObjectRequest = {
      Bucket: BUCKET_NAME,
      Key: key,
    };

    await s3.deleteObject(deleteParams).promise();
  } catch (error) {
    console.error("Error deleting from MinIO:", error);
    throw new Error(`Failed to delete file: ${error}`);
  }
};

/**
 * Get a file from MinIO
 * @param key - The key/path of the file to retrieve
 * @returns Promise with file data
 */
export const getFromMinIO = async (
  key: string
): Promise<AWS.S3.GetObjectOutput> => {
  try {
    const getParams: AWS.S3.GetObjectRequest = {
      Bucket: BUCKET_NAME,
      Key: key,
    };

    return await s3.getObject(getParams).promise();
  } catch (error) {
    console.error("Error getting file from MinIO:", error);
    throw new Error(`Failed to get file: ${error}`);
  }
};

/**
 * Generate a presigned URL for file access
 * @param key - The key/path of the file
 * @param expiresIn - Expiration time in seconds (default: 3600 = 1 hour)
 * @returns presigned URL
 */
export const getPresignedUrl = (
  key: string,
  expiresIn: number = 3600
): string => {
  try {
    const params: AWS.S3.GetObjectRequest = {
      Bucket: BUCKET_NAME,
      Key: key,
    };

    return s3.getSignedUrl("getObject", params);
  } catch (error) {
    console.error("Error generating presigned URL:", error);
    throw new Error(`Failed to generate presigned URL: ${error}`);
  }
};

/**
 * List files in a specific prefix/folder
 * @param prefix - The prefix/folder path to list files from
 * @returns Promise with list of objects
 */
export const listFiles = async (
  prefix?: string
): Promise<AWS.S3.ObjectList> => {
  try {
    const listParams: AWS.S3.ListObjectsV2Request = {
      Bucket: BUCKET_NAME,
      Prefix: prefix || "",
    };

    const result = await s3.listObjectsV2(listParams).promise();
    return result.Contents || [];
  } catch (error) {
    console.error("Error listing files from MinIO:", error);
    throw new Error(`Failed to list files: ${error}`);
  }
};

/**
 * Ensure the bucket exists, create if it doesn't
 */
const ensureBucketExists = async (): Promise<void> => {
  try {
    await s3.headBucket({ Bucket: BUCKET_NAME }).promise();
  } catch (error) {
    // Bucket doesn't exist, create it
    try {
      await s3.createBucket({ Bucket: BUCKET_NAME }).promise();
    } catch (createError) {
      console.error("Error creating bucket:", createError);
      throw new Error(`Failed to create bucket: ${createError}`);
    }
  }
};

/**
 * Get content type based on file type
 * @param file - File object or Buffer
 * @returns MIME type string
 */
const getContentType = (file: File | Buffer): string => {
  if (file instanceof File) {
    return file.type || "application/octet-stream";
  }

  // For Buffer, we'll need to detect the type
  // You might want to use a library like 'file-type' for this
  return "application/octet-stream";
};

/**
 * Generate a unique key for file upload
 * @param originalName - Original file name
 * @param prefix - Optional prefix for organization
 * @returns Unique key string
 */
export const generateUniqueKey = (
  originalName: string,
  prefix?: string
): string => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  const extension = originalName.split(".").pop();
  const baseName = originalName.split(".").slice(0, -1).join(".");

  const key = `${
    prefix || "uploads"
  }/${timestamp}-${randomString}-${baseName}.${extension}`;
  return key;
};
